import { Edit3, Trash2, Phone, Mail, Eye, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

var AVATAR_COLORS = ["bg-[#3B82F6]", "bg-[#F26A4B]", "bg-amber-500", "bg-purple-500", "bg-emerald-500", "bg-pink-500"];

function getInitials(name) {
  if (!name) return "C";
  return name.split(" ").map(function(n) { return n[0]; }).join("").slice(0, 2).toUpperCase();
}

function formatDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch(e) { return "-"; }
}

function PendingBadge({ client }) {
  var pending = client.pending_fields;
  if (!pending || pending.length === 0) return null;
  var labels = pending.map(function(p) { return p.label; }).join(", ");
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
      title={"Pendente: " + labels} data-testid={"pending-badge-" + client.id}>
      <AlertCircle className="w-3 h-3" />{pending.length} pendencia{pending.length > 1 ? "s" : ""}
    </span>
  );
}

export function ClientsTable({ clients, isAdmin, isSuperAdmin, onEdit, onDelete, selected, onToggle, onToggleAll }) {
  var navigate = useNavigate();
  var allSelected = clients.length > 0 && selected.length === clients.length;

  return (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            {isSuperAdmin && (
              <th className="py-3 px-3 w-10">
                <input type="checkbox" checked={allSelected} onChange={function() { onToggleAll(); }}
                  className="w-4 h-4 rounded border-gray-300 text-[#3B82F6] focus:ring-[#3B82F6]" data-testid="select-all-checkbox" />
              </th>
            )}
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Contato</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">CPF/CNPJ</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Corretor</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Desde</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Acoes</th>
          </tr>
        </thead>
        <tbody>
          {clients.map(function(c, idx) {
            var initials = getInitials(c.name);
            var avatarCls = AVATAR_COLORS[idx % AVATAR_COLORS.length];
            var isSelected = selected.indexOf(c.id) !== -1;
            return (
              <tr key={c.id} className={"border-b border-gray-50 hover:bg-[#3B82F6]/[0.02] cursor-pointer " + (isSelected ? "bg-blue-50/50" : "")} data-testid={"client-row-" + c.id}
                onClick={function() { navigate("/clientes/" + c.id); }}>
                {isSuperAdmin && (
                  <td className="py-3 px-3" onClick={function(e) { e.stopPropagation(); }}>
                    <input type="checkbox" checked={isSelected} onChange={function() { onToggle(c.id); }}
                      className="w-4 h-4 rounded border-gray-300 text-[#3B82F6] focus:ring-[#3B82F6]" data-testid={"select-checkbox-" + c.id} />
                  </td>
                )}
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className={"w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 " + avatarCls}>
                      <span className="text-white text-xs font-semibold">{initials}</span>
                    </div>
                    <p className="text-sm font-medium text-[#3B82F6] hover:underline" data-testid={"client-name-link-" + c.id}>{c.name}</p>
                    <PendingBadge client={c} />
                  </div>
                </td>
                <td className="py-3 px-4">
                  {c.email && <div className="flex items-center gap-1.5 text-xs text-gray-500"><Mail className="w-3 h-3" />{c.email}</div>}
                  {c.phone && <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5"><Phone className="w-3 h-3" />{c.phone}</div>}
                </td>
                <td className="py-3 px-4 text-xs text-gray-500">{c.cnpj || c.cpf || "-"}</td>
                <td className="py-3 px-4 text-xs text-gray-500">{c.broker_name || "-"}</td>
                <td className="py-3 px-4 text-xs text-gray-400">{formatDate(c.created_at)}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-end gap-1" onClick={function(e) { e.stopPropagation(); }}>
                    <button onClick={function() { navigate("/clientes/" + c.id); }} className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-[#3B82F6]" data-testid={"client-view-" + c.id}><Eye className="w-4 h-4" /></button>
                    <button onClick={function() { onEdit(c); }} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-[#3B82F6]" data-testid={"client-edit-" + c.id}><Edit3 className="w-4 h-4" /></button>
                    {isAdmin && <button onClick={function() { onDelete(c); }} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500" data-testid={"client-delete-" + c.id}><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ClientsMobile({ clients, isAdmin, isSuperAdmin, onEdit, onDelete, selected, onToggle }) {
  var navigate = useNavigate();

  return (
    <div className="md:hidden divide-y divide-gray-50">
      {clients.map(function(c, idx) {
        var initials = getInitials(c.name);
        var avatarCls = AVATAR_COLORS[idx % AVATAR_COLORS.length];
        var isSelected = selected.indexOf(c.id) !== -1;
        return (
          <div key={c.id} className={"p-4 cursor-pointer " + (isSelected ? "bg-blue-50/50" : "")} data-testid={"client-card-" + c.id}
            onClick={function() { navigate("/clientes/" + c.id); }}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-3">
                {isSuperAdmin && (
                  <div onClick={function(e) { e.stopPropagation(); }}>
                    <input type="checkbox" checked={isSelected} onChange={function() { onToggle(c.id); }}
                      className="w-4 h-4 rounded border-gray-300 text-[#3B82F6] focus:ring-[#3B82F6]" />
                  </div>
                )}
                <div className={"w-9 h-9 rounded-full flex items-center justify-center " + avatarCls}>
                  <span className="text-white text-xs font-semibold">{initials}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#3B82F6]">{c.name}</p>
                  <PendingBadge client={c} />
                  <p className="text-xs text-gray-400">{c.email}</p>
                </div>
              </div>
              <div className="flex gap-1" onClick={function(e) { e.stopPropagation(); }}>
                <button onClick={function() { onEdit(c); }} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"><Edit3 className="w-4 h-4" /></button>
                {isAdmin && <button onClick={function() { onDelete(c); }} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
